import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminModal, EmptyState, SkeletonLoader, StatusBadge } from "../../components/admin";
import { Button, ConfirmModal, InputField, Pagination } from "../../components/common";
import AdminLayout from "../../components/layout/AdminLayout";
import { useToast } from "../../contexts/ToastContext";
import adminService, {
  AdminApplication,
  UpdateApplicationStatusRequest,
} from "../../services/api/adminService";

const ApplicationManagement: React.FC = () => {
  const [applications, setApplications] = useState<AdminApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<AdminApplication | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingApplication, setDeletingApplication] = useState<AdminApplication | null>(null);

  const { showError, showSuccess } = useToast();
  const limit = 20;

  const statusOptions = [
    { value: "DRAFT", label: "Draft" },
    { value: "PENDING_REVIEW", label: "Pending Review" },
    { value: "APPROVED", label: "Approved" },
    { value: "SUBMITTED", label: "Submitted" },
    { value: "UNDER_REVIEW", label: "Under Review" },
    { value: "ACCEPTED", label: "Accepted" },
    { value: "REJECTED", label: "Rejected" },
    { value: "WAITLISTED", label: "Waitlisted" },
    { value: "WITHDRAWN", label: "Withdrawn" },
  ];

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, selectedStatus]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, limit };
      if (searchTerm) params.search = searchTerm;
      if (selectedStatus) params.status = selectedStatus;

      const response = await adminService.getApplications(params);
      setApplications(response.data.applications);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.message || "Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const openStatusModal = (application: AdminApplication) => {
    setSelectedApplication(application);
    setNewStatus(application.status);
    setReviewNotes("");
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedApplication(null);
    setNewStatus("");
    setReviewNotes("");
  };

  const handleStatusUpdate = async () => {
    if (!selectedApplication) return;
    try {
      setUpdateLoading(true);
      const request: UpdateApplicationStatusRequest = {
        status: newStatus as any,
        reviewNotes: reviewNotes || undefined,
      };
      await adminService.updateApplicationStatus(selectedApplication.id, request);
      showSuccess("Application status updated successfully");
      closeStatusModal();
      fetchApplications();
    } catch (err: any) {
      showError(err.message || "Failed to update application status");
    } finally {
      setUpdateLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDeleteApplication = (application: AdminApplication) => {
    setDeletingApplication(application);
    setShowDeleteModal(true);
  };

  const confirmDeleteApplication = async () => {
    if (!deletingApplication) return;
    setShowDeleteModal(false);
    try {
      await adminService.deleteApplication(deletingApplication.id);
      showSuccess("Application deleted successfully");
      fetchApplications();
    } catch (err: any) {
      showError(err.message || "Failed to delete application");
    } finally {
      setDeletingApplication(null);
    }
  };

  const activeFilters = [
    selectedStatus && { label: statusOptions.find(o => o.value === selectedStatus)?.label || selectedStatus, clear: () => setSelectedStatus("") },
    searchTerm && { label: `"${searchTerm}"`, clear: () => setSearchTerm("") },
  ].filter(Boolean) as { label: string; clear: () => void }[];

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Applications</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and manage student applications
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              name="search"
              type="text"
              label="Search"
              placeholder="Application number, name, or course..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow"
              >
                <option value="">All Status</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchApplications} className="w-full" disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </Button>
            </div>
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Filters:</span>
              {activeFilters.map((f, i) => (
                <button
                  key={i}
                  onClick={f.clear}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-cyan-50 text-cyan-700 rounded-full hover:bg-cyan-100 transition-colors"
                >
                  {f.label}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Application
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Student
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Course & University
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Documents
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Submitted
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && applications.length === 0 ? (
                  <SkeletonLoader variant="table-row" count={5} columns={7} />
                ) : (
                  applications.map((application) => (
                    <tr
                      key={application.id}
                      className="border-b border-gray-50 hover:bg-cyan-50/30 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">
                          {application.applicationNumber}
                        </p>
                        <p className="text-xs text-gray-400">{application.nationality}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">
                          {application.givenNames} {application.familyName}
                        </p>
                        <p className="text-xs text-gray-400">{application.email}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">
                          {application.course.title}
                        </p>
                        <p className="text-xs text-gray-400">
                          {application.course.university.name} ({application.course.university.shortName})
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={application.status} type="application" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-700">
                          {application.documents.length} docs
                        </p>
                        <p className="text-xs text-gray-400">
                          {application.documents.filter((doc) => doc.isVerified).length} verified
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {application.submittedAt
                          ? formatDate(application.submittedAt)
                          : <span className="text-gray-300">--</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-0.5">
                          {/* View application */}
                          <Link
                            to={`/admin/applications/${application.id}`}
                            title="View application"
                            className="p-1.5 rounded-lg text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          {/* Update status */}
                          <button
                            onClick={() => openStatusModal(application)}
                            title="Update status"
                            className="p-1.5 rounded-lg text-amber-600 hover:text-amber-800 hover:bg-amber-50 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {/* View user */}
                          <Link
                            to={`/admin/users/${application.user.id}`}
                            title="View user"
                            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </Link>
                          {/* Delete application */}
                          <button
                            onClick={() => handleDeleteApplication(application)}
                            title="Delete application"
                            className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((currentPage - 1) * limit) + 1}-{Math.min(currentPage * limit, pagination.total)} of {pagination.total}
            </p>
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              onPageChange={setCurrentPage}
              hasNextPage={pagination.hasNext}
              hasPrevPage={pagination.hasPrev}
            />
          </div>
        )}

        {/* Empty State */}
        {applications.length === 0 && !loading && (
          <EmptyState
            title="No applications found"
            description="Try adjusting your search criteria or refresh the page."
            actionLabel="Clear Filters"
            onAction={() => { setSearchTerm(""); setSelectedStatus(""); }}
          />
        )}

        {/* Status Update Modal */}
        <AdminModal
          isOpen={showStatusModal && !!selectedApplication}
          onClose={closeStatusModal}
          title="Update Application Status"
          size="sm"
        >
          {selectedApplication && (
            <div className="space-y-5">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {selectedApplication.applicationNumber}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedApplication.givenNames} {selectedApplication.familyName}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Review Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow resize-none"
                  placeholder="Add notes about this status change..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={closeStatusModal}
                  disabled={updateLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={updateLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
                >
                  {updateLoading ? "Updating..." : "Update Status"}
                </button>
              </div>
            </div>
          )}
        </AdminModal>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Application"
        message={`Are you sure you want to permanently delete application ${deletingApplication?.applicationNumber} for ${deletingApplication?.givenNames} ${deletingApplication?.familyName}? This will remove all documents and data associated with this application and cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteApplication}
        onCancel={() => { setShowDeleteModal(false); setDeletingApplication(null); }}
        variant="danger"
      />
    </AdminLayout>
  );
};

export default ApplicationManagement;
